import { useState, useEffect, useRef } from 'react'
import type { TreatmentDetail } from '../../store/treatmentsSlice'

interface FollowUpProps {
  treatment: TreatmentDetail
  onUpdate: (data: Partial<TreatmentDetail>) => Promise<void>
  onCreateFollowUp: () => Promise<void>
}

export function FollowUp({ treatment, onUpdate, onCreateFollowUp }: FollowUpProps) {
  const [editing, setEditing] = useState(false)
  const normalizeDate = (d: string | null) => d ? d.toString().split('T')[0] : ''
  const [followUpDate, setFollowUpDate] = useState(normalizeDate(treatment.follow_up_date))
  const [followUpNotes, setFollowUpNotes] = useState(treatment.follow_up_notes || '')
  const dateRef = useRef<HTMLInputElement>(null)
  const notesRef = useRef<HTMLTextAreaElement>(null)

  const editingRef = useRef(false)
  editingRef.current = editing

  useEffect(() => {
    if (!editingRef.current) {
      setFollowUpDate(normalizeDate(treatment.follow_up_date))
      setFollowUpNotes(treatment.follow_up_notes || '')
    }
  }, [treatment])

  const handleSave = async () => {
    const dateValue = dateRef.current?.value || followUpDate
    const notesValue = notesRef.current?.value ?? followUpNotes
    await onUpdate({
      follow_up_date: dateValue || null,
      follow_up_notes: notesValue,
    })
    setEditing(false)
  }

  return (
    <div className="bg-surface rounded-[6px] border border-border-light" data-testid="follow-up-section">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-light">
        <h2 className="text-[14px] font-semibold text-text-primary">Follow-Up</h2>
        {!editing ? (
          <button
            data-testid="edit-follow-up-button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              data-testid="follow-up-cancel-button"
              onClick={() => {
                setEditing(false)
                setFollowUpDate(normalizeDate(treatment.follow_up_date))
                setFollowUpNotes(treatment.follow_up_notes || '')
              }}
              className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
            >
              Cancel
            </button>
            <button
              data-testid="follow-up-save-button"
              onClick={handleSave}
              className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
            >
              Save
            </button>
          </div>
        )}
      </div>

      <div className="p-4 max-sm:p-3 flex flex-col gap-4">
        <div>
          <label className="block text-[12px] text-text-muted mb-1">Follow-Up Date</label>
          {editing ? (
            <input
              ref={dateRef}
              data-testid="follow-up-date-input"
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
            />
          ) : (
            <p className="text-[13px] text-text-primary" data-testid="follow-up-date-value">
              {treatment.follow_up_date ? (
                new Date(treatment.follow_up_date.toString().split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              ) : (
                <span className="text-text-disabled">No follow-up date set</span>
              )}
            </p>
          )}
        </div>

        <div>
          <label className="block text-[12px] text-text-muted mb-1">Follow-Up Notes</label>
          {editing ? (
            <textarea
              ref={notesRef}
              data-testid="follow-up-notes-input"
              value={followUpNotes}
              onChange={(e) => setFollowUpNotes(e.target.value)}
              placeholder="Enter follow-up notes..."
              rows={3}
              className="w-full px-2 py-1.5 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent resize-y"
            />
          ) : (
            <p className="text-[13px] text-text-primary whitespace-pre-wrap" data-testid="follow-up-notes-value">
              {treatment.follow_up_notes || <span className="text-text-disabled">No follow-up notes</span>}
            </p>
          )}
        </div>

        <div>
          <button
            data-testid="create-follow-up-treatment-button"
            onClick={onCreateFollowUp}
            className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
          >
            Create Follow-Up Treatment
          </button>
        </div>
      </div>
    </div>
  )
}
